import { Server } from '@modelcontextprotocol/sdk/server/index.js';
import { StdioServerTransport } from '@modelcontextprotocol/sdk/server/stdio.js';
import {
  CallToolRequestSchema,
  ListToolsRequestSchema,
  ListResourcesRequestSchema,
  ReadResourceRequestSchema,
} from '@modelcontextprotocol/sdk/types.js';

import { logger } from './lib/logger.js';
import { getConfig } from './config.js';

// Tools
import { handleValidateConventions, validateConventionsTool } from './tools/validate-conventions.js';
import { handleCheckMigrations, checkMigrationsTool } from './tools/check-migrations.js';
import { handleScaffoldExtension, scaffoldExtensionTool } from './tools/scaffold-extension.js';
import { handleApiDocs, apiDocsTool } from './tools/api-docs.js';
import { handleSuggestMigration, suggestMigrationTool } from './tools/suggest-migration.js';
import { handleGeneratePermissions, generatePermissionsTool } from './tools/generate-permissions.js';

// Test Tools
import { handleScaffoldTests, scaffoldTestsTool } from './tools/scaffold-tests.js';
import { handleAnalyzeTestCoverage, analyzeTestCoverageTool } from './tools/analyze-test-coverage.js';
import { handleValidateTestConventions, validateTestConventionsTool } from './tools/validate-test-conventions.js';
import { handleSuggestTestScenarios, suggestTestScenariosTool } from './tools/suggest-test-scenarios.js';

// Frontend Route Tools
import { handleScaffoldApiClient, scaffoldApiClientTool } from './tools/scaffold-api-client.js';
import { handleScaffoldRoutes, scaffoldRoutesTool } from './tools/scaffold-routes.js';
import { handleValidateFrontendRoutes, validateFrontendRoutesTool } from './tools/validate-frontend-routes.js';

// Frontend Extension Tools
import { handleScaffoldFrontendExtension, scaffoldFrontendExtensionTool } from './tools/scaffold-frontend-extension.js';
import { handleAnalyzeExtensionPoints, analyzeExtensionPointsTool } from './tools/analyze-extension-points.js';

// Security & Code Quality Tools
import { handleValidateSecurity, validateSecurityTool } from './tools/validate-security.js';
import { handleAnalyzeCodeQuality, analyzeCodeQualityTool } from './tools/analyze-code-quality.js';

// Hierarchy Analysis Tools
import { handleAnalyzeHierarchyPatterns, analyzeHierarchyPatternsTool } from './tools/analyze-hierarchy-patterns.js';

// Code Review Tool
import { handleReviewCode, reviewCodeTool } from './tools/review-code.js';

// Resources
import { getConventionsResource, conventionsResourceTemplate } from './resources/conventions.js';
import { getProjectInfoResource, projectInfoResourceTemplate } from './resources/project-info.js';
import { getApiEndpointsResource, apiEndpointsResourceTemplate } from './resources/api-endpoints.js';
import { getDbSchemaResource, dbSchemaResourceTemplate } from './resources/db-schema.js';
import { getEntitiesResource, entitiesResourceTemplate } from './resources/entities.js';

export async function createServer(): Promise<Server> {
  const config = await getConfig();

  const server = new Server(
    {
      name: 'smartstack-mcp',
      version: '1.10.0',
    },
    {
      capabilities: {
        tools: {},
        resources: {},
      },
    }
  );

  // ============================================================================
  // Tools Registration
  // ============================================================================

  server.setRequestHandler(ListToolsRequestSchema, async () => {
    logger.debug('Listing tools');
    return {
      tools: [
        validateConventionsTool,
        checkMigrationsTool,
        scaffoldExtensionTool,
        apiDocsTool,
        suggestMigrationTool,
        generatePermissionsTool,
        // Test Tools
        scaffoldTestsTool,
        analyzeTestCoverageTool,
        validateTestConventionsTool,
        suggestTestScenariosTool,
        // Frontend Route Tools
        scaffoldApiClientTool,
        scaffoldRoutesTool,
        validateFrontendRoutesTool,
        // Frontend Extension Tools
        scaffoldFrontendExtensionTool,
        analyzeExtensionPointsTool,
        // Security & Code Quality Tools
        validateSecurityTool,
        analyzeCodeQualityTool,
        // Hierarchy Analysis Tools
        analyzeHierarchyPatternsTool,
        // Code Review Tool
        reviewCodeTool,
      ],
    };
  });

  server.setRequestHandler(CallToolRequestSchema, async (request) => {
    const { name, arguments: args } = request.params;
    const startTime = Date.now();

    logger.toolStart(name, args);

    try {
      let result: string;

      switch (name) {
        case 'validate_conventions':
          result = await handleValidateConventions(args, config);
          break;

        case 'check_migrations':
          result = await handleCheckMigrations(args, config);
          break;

        case 'scaffold_extension':
          result = await handleScaffoldExtension(args, config);
          break;

        case 'api_docs':
          result = await handleApiDocs(args, config);
          break;

        case 'suggest_migration':
          result = await handleSuggestMigration(args, config);
          break;

        case 'generate_permissions':
          result = await handleGeneratePermissions(args, config);
          break;

        // Test Tools
        case 'scaffold_tests':
          result = await handleScaffoldTests(args, config);
          break;

        case 'analyze_test_coverage':
          result = await handleAnalyzeTestCoverage(args, config);
          break;

        case 'validate_test_conventions':
          result = await handleValidateTestConventions(args, config);
          break;

        case 'suggest_test_scenarios':
          result = await handleSuggestTestScenarios(args, config);
          break;

        // Frontend Route Tools
        case 'scaffold_api_client':
          result = await handleScaffoldApiClient(args ?? {}, config);
          break;

        case 'scaffold_routes':
          result = await handleScaffoldRoutes(args ?? {}, config);
          break;

        case 'validate_frontend_routes':
          result = await handleValidateFrontendRoutes(args ?? {}, config);
          break;

        // Frontend Extension Tools
        case 'scaffold_frontend_extension':
          result = await handleScaffoldFrontendExtension(args ?? {}, config);
          break;

        case 'analyze_extension_points':
          result = await handleAnalyzeExtensionPoints(args ?? {}, config);
          break;

        // Security & Code Quality Tools
        case 'validate_security':
          result = await handleValidateSecurity(args ?? {}, config);
          break;

        case 'analyze_code_quality':
          result = await handleAnalyzeCodeQuality(args ?? {}, config);
          break;

        // Hierarchy Analysis Tools
        case 'analyze_hierarchy_patterns':
          result = await handleAnalyzeHierarchyPatterns(args ?? {}, config);
          break;

        // Code Review Tool
        case 'review_code':
          result = await handleReviewCode(args ?? {}, config);
          break;

        default:
          throw new Error(`Unknown tool: ${name}`);
      }

      logger.toolEnd(name, true, Date.now() - startTime);

      return {
        content: [
          {
            type: 'text',
            text: result,
          },
        ],
      };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      logger.toolError(name, err);

      return {
        content: [
          {
            type: 'text',
            text: `Error: ${err.message}`,
          },
        ],
        isError: true,
      };
    }
  });

  // ============================================================================
  // Resources Registration
  // ============================================================================

  server.setRequestHandler(ListResourcesRequestSchema, async () => {
    logger.debug('Listing resources');
    return {
      resources: [
        conventionsResourceTemplate,
        projectInfoResourceTemplate,
        apiEndpointsResourceTemplate,
        dbSchemaResourceTemplate,
        entitiesResourceTemplate,
      ],
    };
  });

  server.setRequestHandler(ReadResourceRequestSchema, async (request) => {
    const { uri } = request.params;
    logger.debug('Reading resource', { uri });

    try {
      let content: string;
      let mimeType = 'text/markdown';

      if (uri === 'smartstack://conventions') {
        content = await getConventionsResource(config);
      } else if (uri === 'smartstack://project') {
        content = await getProjectInfoResource(config);
      } else if (uri.startsWith('smartstack://api/')) {
        const endpoint = uri.replace('smartstack://api/', '');
        content = await getApiEndpointsResource(config, endpoint);
      } else if (uri.startsWith('smartstack://schema/')) {
        const table = uri.replace('smartstack://schema/', '');
        content = await getDbSchemaResource(config, table);
      } else if (uri.startsWith('smartstack://entities/')) {
        const entityFilter = uri.replace('smartstack://entities/', '');
        content = await getEntitiesResource(config, entityFilter || undefined);
      } else if (uri === 'smartstack://entities') {
        content = await getEntitiesResource(config);
      } else {
        throw new Error(`Unknown resource: ${uri}`);
      }

      return {
        contents: [
          {
            uri,
            mimeType,
            text: content,
          },
        ],
      };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      logger.error('Resource read failed', { uri, error: err.message });
      throw err;
    }
  });

  return server;
}

export async function runServer(): Promise<void> {
  logger.info('Starting SmartStack MCP Server...');

  const server = await createServer();
  const transport = new StdioServerTransport();

  await server.connect(transport);

  logger.info('SmartStack MCP Server running on stdio');
}
